import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getIdToken,
  getRefreshToken,
  isTokenValid,
  redirectToLogin,
  exchangeCodeForTokens,
  buildLogoutUrl,
  clearTokens,
  refreshTokens,
  revokeRefreshToken,
  sanitizeReturnPath,
} from '../services/cognitoAuth';
import { queryClient } from '../queries/queryClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // COL-88 (multi-tenancy, security-critical): react-query cache keys are
  // namespaced by user id (see queries/keys.js), which already stops one
  // user's queries from ever reading a cache entry written for another. This
  // is the belt-and-braces second layer: whenever the *resolved* auth
  // identity actually changes from one real user to a *different* real
  // user — wipe the entire query cache outright. That way even a future
  // hook that forgets to scope its key can't leak data across a user
  // switch.
  //
  // Tracks the *last known real* user id (never overwritten by a `null`
  // resolution — only ever moves from one real id to another), rather than
  // just the immediately-previous value. Only fires when that last real id
  // was a *different* real id than the current one — i.e. an actual A -> B
  // switch. There is no previous user's data to protect on any transition
  // *into* a real id that isn't preceded by a *different* real id
  // (undefined -> A, null -> A, or A -> null -> A), so those must not
  // clear. Deliberately does NOT special-case "the previous value was
  // null" as "nothing to protect" — an A -> null -> B path (e.g. a token
  // expiring with no refresh token, or any future in-app re-auth flow that
  // doesn't force a full page reload) must still clear, because it's a
  // genuine A -> B switch with an unauthenticated gap in the middle, not a
  // first-ever resolution.
  //
  // The undefined/null -> A suppression is load-bearing, not just precise:
  // on the OAuth callback route, AuthProvider's own mount-time
  // resolveAuth() resolves FAST to unauthenticated (no token stored yet —
  // exchangeCodeForTokens hasn't run) before Callback.jsx's
  // handleCallback() lands the real user moments later via a second
  // checkAuth() call. That produces a spurious `null -> real-id`
  // transition on every single fresh login. If that transition cleared
  // the cache, it would do so right as the just-navigated-to page's first
  // query is starting (or has just started) — wiping data the query is
  // relying on, with no automatic refetch to recover, leaving the UI
  // stuck empty until a hard reload (which resolves auth in one
  // synchronous step and never hits this race). Confirmed via live
  // browser verification and reproduced deterministically in
  // AuthContext.test.jsx.
  const lastRealUserIdRef = useRef(undefined);
  useEffect(() => {
    if (isLoading) return;
    const currentUserId = user?.id ?? null;
    if (currentUserId && lastRealUserIdRef.current && lastRealUserIdRef.current !== currentUserId) {
      queryClient.clear();
    }
    if (currentUserId) {
      lastRealUserIdRef.current = currentUserId;
    }
  }, [user?.id, isLoading]);

  const checkAuth = useCallback(() => {
    const token = getIdToken();
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const isValid = isTokenValid(token);

      if (isValid) {
        // Parse cognito:groups claim — API Gateway serializes arrays as strings like "[admin]"
        const groupsClaim = payload['cognito:groups'] || '';
        let groups = [];
        if (Array.isArray(groupsClaim)) {
          groups = groupsClaim;
        } else if (typeof groupsClaim === 'string' && groupsClaim) {
          groups = groupsClaim.replace(/^\[|\]$/g, '').split(',').map(g => g.trim()).filter(Boolean);
        }

        setIsAuthenticated(true);
        setUser({
          id: payload.sub,
          email: payload.email,
          name: payload.name || payload.email?.split('@')[0],
          isAdmin: groups.includes('admin'),
        });
        return true;
      }
    } catch {
      // Invalid token
    }

    setIsAuthenticated(false);
    setUser(null);
    return false;
  }, []);

  /**
   * Resolve auth state on mount / when returning to the tab. Unlike
   * checkAuth() (a pure sync read of the current token), this attempts a
   * refresh first when the stored id token is missing or expired but a
   * refresh token is still available. Without this, reloading or returning
   * to the tab after the ~1h access-token lifetime would hard-redirect to
   * Cognito hosted-UI login before ApiService ever gets a chance to refresh
   * on a 401 — the exact scenario this ticket exists to fix.
   */
  const resolveAuth = useCallback(async () => {
    const token = getIdToken();
    if (token && isTokenValid(token)) {
      return checkAuth();
    }

    if (getRefreshToken()) {
      try {
        await refreshTokens();
        return checkAuth();
      } catch {
        // Refresh failed — fall through to unauthenticated below.
      }
    }

    setIsAuthenticated(false);
    setUser(null);
    return false;
  }, [checkAuth]);

  const login = useCallback(async (returnUrl = '/') => {
    await redirectToLogin(returnUrl);
  }, []);

  const handleCallback = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    // `state` round-trips through Cognito's hosted UI — an attacker can send
    // a victim straight to the /oauth2/authorize endpoint with a crafted
    // `state`, so it must be treated as untrusted input and never navigated
    // to unvalidated (COL-154). It's also `encodeURIComponent`-ed on the way
    // out (redirectToLogin) since Cognito doesn't safely round-trip a raw
    // `&`/`=`/`?`-bearing value — decode before validating. A malformed
    // percent-sequence (accidental or adversarial) throws; treat that as no
    // usable state rather than a hard error on an otherwise-successful login.
    let decodedState = '/';
    const rawState = params.get('state');
    if (rawState) {
      try {
        decodedState = decodeURIComponent(rawState);
      } catch {
        decodedState = '/';
      }
    }
    const state = sanitizeReturnPath(decodedState, '/');
    const error = params.get('error');

    if (error) {
      throw new Error(`OAuth error: ${error}`);
    }

    if (!code) {
      throw new Error('No authorization code received');
    }

    const codeVerifier = sessionStorage.getItem('collections_code_verifier');
    if (!codeVerifier) {
      throw new Error('No code verifier found');
    }

    await exchangeCodeForTokens(code, codeVerifier);
    sessionStorage.removeItem('collections_code_verifier');

    checkAuth();
    return state;
  }, [checkAuth]);

  const logout = useCallback(async () => {
    // Cognito hosted UI logout uses logout_uri parameter
    // The logout_uri must be registered in Cognito's "Allowed sign-out URLs"
    // Clear the query cache immediately (belt-and-braces — the full-page
    // redirect below also resets all JS memory, but don't rely on that).
    queryClient.clear();
    // Revoke the refresh token BEFORE clearing storage — read it first, or
    // there's nothing left to revoke (COL-192). Bounded/best-effort: never
    // blocks the redirect below even if Cognito is unreachable.
    await revokeRefreshToken(getRefreshToken());
    clearTokens();
    window.location.href = buildLogoutUrl('/');
  }, []);

  useEffect(() => {
    let cancelled = false;
    resolveAuth().finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [resolveAuth]);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      user,
      login,
      logout,
      handleCallback,
      checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
